import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';

import {
  MilestoneActionsUnion,
  MilestoneActionTypes,
  MilestoneStatus
} from '@core/actions/milestone.actions';

import { Milestone } from '@app/models/milestone';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { comparerByDate } from '@app/comparers';

export interface State extends EntityState<Milestone> {
  loading: boolean;
  loaded: boolean;
  creating: boolean;
  statuses: MilestoneStatus[];
}

export const adapter: EntityAdapter<Milestone> = createEntityAdapter<Milestone>(
  {
    selectId: (milestone: Milestone) => milestone.id,
    sortComparer: comparerByDate('DESC', 'createdAt')
  }
);

export const INITIAL_STATE: State = adapter.getInitialState({
  loading: false,
  loaded: false,
  creating: false,
  statuses: []
});

export function reducer(
  state: State = INITIAL_STATE,
  action: MilestoneActionsUnion
) {
  switch (action.type) {
    case MilestoneActionTypes.Load:
      return { ...state, loading: true };

    case MilestoneActionTypes.LoadFail:
      return { ...state, loading: false };

    case MilestoneActionTypes.LoadSuccess:
      return { ...state, loading: false, loaded: true };

    case MilestoneActionTypes.Create:
      return { ...state, creating: true };

    case MilestoneActionTypes.CreateSuccess:
    case MilestoneActionTypes.CreateFail:
      return { ...state, creating: false };

    case MilestoneActionTypes.AddAll:
      return adapter.addAll(action.milestones, state);

    case MilestoneActionTypes.AddOne:
      return adapter.addOne(action.milestone, state);

    case MilestoneActionTypes.AddMany:
      return adapter.addMany(action.milestones, state);

    case MilestoneActionTypes.LoadStatusesSuccess:
      return { ...state, statuses: action.statuses };

    default:
      return state;
  }
}

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal
} = adapter.getSelectors();

export const selectLoaded = (state: State) => state.loaded;
export const selectLoading = (state: State) => state.loading;
export const selectCreating = (state: State) => state.creating;

const getState = createFeatureSelector<State>('milestones');

export const getAll = createSelector(
  getState,
  selectAll
);

export const isCreating = createSelector(
  getState,
  selectCreating
);

export const getStatuses = createSelector(
  getState,
  (state: State) => state.statuses
);
